import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Crown, Lock, X, Zap, Gift, Users, Timer, 
  ChevronRight, Sparkles, Check, Shield
} from "lucide-react";
import { Button } from "./ui/button";

// Premium Feature Lock Overlay
export const PremiumLock = ({ feature, children, canAccess = false, onUpgrade }) => {
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  if (canAccess) return children;

  return (
    <div className="relative">
      <div className="blur-sm pointer-events-none select-none">
        {children}
      </div>
      <div 
        className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm rounded-2xl cursor-pointer"
        onClick={() => setShowUpgradePrompt(true)}
      >
        <Lock className="w-8 h-8 text-white/60 mb-2" />
        <p className="text-white/80 font-medium text-sm">{feature}</p>
        <p className="text-[#00f2ea] text-xs mt-1">Click to unlock</p>
      </div>
      
      <UpgradeModal 
        isOpen={showUpgradePrompt} 
        onClose={() => setShowUpgradePrompt(false)}
        feature={feature}
      />
    </div>
  );
};

// Upgrade Prompt Modal
export const UpgradeModal = ({ isOpen, onClose, feature }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="glass rounded-2xl p-6 max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00f2ea] to-[#7000ff] flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-white" />
            </div>
            
            <h3 className="text-xl font-bold mb-2">Unlock {feature}</h3>
            <p className="text-white/60 mb-6">
              Upgrade to Premium to access {feature.toLowerCase()} and all other premium features.
            </p>
            
            <div className="space-y-3">
              <Link to="/subscription" className="block">
                <Button className="w-full bg-[#00f2ea] hover:bg-[#00d4d4] text-black font-semibold rounded-xl py-6">
                  <Zap className="w-5 h-5 mr-2" />
                  Upgrade to Premium - $14.99/year
                </Button>
              </Link>
              
              <Link to="/subscription" className="block">
                <Button variant="outline" className="w-full rounded-xl py-5 border-white/20">
                  <Timer className="w-4 h-4 mr-2" />
                  Start 7-Day Free Trial
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Countdown Timer for Discount
export const DiscountCountdown = ({ endDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const end = new Date(endDate);
      const diff = end - now;

      if (diff <= 0) {
        setIsActive(false);
        return;
      }

      setIsActive(true);
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((diff % (1000 * 60)) / 1000)
      });
    };

    calculateTimeLeft();
    const interval = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  if (!isActive) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-[#ff0080] to-[#7000ff] rounded-xl p-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Gift className="w-5 h-5 text-white" />
          <span className="font-bold text-white">20% OFF Holiday Sale!</span>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {[
              { value: timeLeft.days, label: 'D' },
              { value: timeLeft.hours, label: 'H' },
              { value: timeLeft.minutes, label: 'M' },
              { value: timeLeft.seconds, label: 'S' }
            ].map((item) => (
              <div key={`timer-${item.label}`} className="bg-black/30 rounded-lg px-2 py-1 min-w-[40px] text-center">
                <span className="text-lg font-bold text-white">{String(item.value).padStart(2, '0')}</span>
                <span className="text-xs text-white/60 ml-0.5">{item.label}</span>
              </div>
            ))}
          </div>
          
          <Link to="/subscription">
            <Button size="sm" className="bg-white text-[#ff0080] hover:bg-white/90 font-semibold rounded-lg">
              Claim Offer
            </Button>
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

// Social Proof Counter
export const SocialProofBanner = ({ count = 0 }) => {
  const [displayCount, setDisplayCount] = useState(0);

  useEffect(() => {
    // Animate count up
    const duration = 2000;
    const steps = 50;
    const increment = count / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= count) {
        setDisplayCount(count);
        clearInterval(timer);
      } else {
        setDisplayCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [count]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex items-center gap-2 text-sm text-white/60"
    >
      <Users className="w-4 h-4 text-[#00f2ea]" />
      <span>
        <span className="text-white font-semibold">{displayCount.toLocaleString()}</span> people subscribed this month
      </span>
    </motion.div>
  );
};

// Premium Badge for Users
export const PremiumBadge = ({ size = "sm" }) => {
  const sizes = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-5 h-5",
    lg: "w-6 h-6"
  };

  return (
    <div className="inline-flex items-center" title="Premium Member">
      <Crown className={`${sizes[size]} text-yellow-400`} />
    </div>
  );
};

// Try Premium Button
export const TryPremiumButton = ({ feature, className = "" }) => {
  return (
    <Link to="/subscription">
      <Button 
        size="sm" 
        className={`bg-gradient-to-r from-[#00f2ea] to-[#7000ff] hover:opacity-90 text-white font-medium rounded-lg ${className}`}
      >
        <Sparkles className="w-4 h-4 mr-1" />
        Try Premium
      </Button>
    </Link>
  );
};

// Weather Event Promotion Banner
export const WeatherEventPromo = ({ event, discount }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-4"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="font-bold text-yellow-400">{event}</p>
          <p className="text-sm text-white/70">Get {discount}% off Premium to track severe weather!</p>
        </div>
        <Link to="/subscription">
          <Button size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg">
            Get Deal
          </Button>
        </Link>
      </div>
    </motion.div>
  );
};

// Referral Card Component
export const ReferralCard = ({ referralCode, referralCount = 0 }) => {
  const [copied, setCopied] = useState(false);
  
  const copyCode = () => {
    navigator.clipboard.writeText(`https://theforecasttracker.com/ref/${referralCode}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-[#00f2ea]/20 flex items-center justify-center">
          <Gift className="w-5 h-5 text-[#00f2ea]" />
        </div>
        <div>
          <h3 className="font-bold">Refer Friends, Get Rewards!</h3>
          <p className="text-sm text-white/60">Give $3, Get $3 for each friend</p>
        </div>
      </div>
      
      <div className="bg-black/30 rounded-xl p-3 flex items-center justify-between mb-4">
        <code className="text-[#00f2ea] text-sm">{referralCode}</code>
        <Button size="sm" variant="ghost" onClick={copyCode}>
          {copied ? <Check className="w-4 h-4 text-green-400" /> : "Copy"}
        </Button>
      </div>
      
      <div className="flex items-center justify-between text-sm">
        <span className="text-white/60">Friends referred:</span>
        <span className="font-bold text-[#00f2ea]">{referralCount}</span>
      </div>
    </div>
  );
};

// First-Time User Onboarding Modal
export const OnboardingModal = ({ isOpen, onClose, onStartTrial }) => {
  const [step, setStep] = useState(0);
  
  const steps = [
    {
      title: "Welcome to TheForecastTracker! 🌤️",
      description: "Your personal weather companion with AI-powered insights.",
      image: "🌍"
    },
    {
      title: "15-Day Forecasts",
      description: "Plan ahead with extended weather predictions.",
      image: "📅"
    },
    {
      title: "3-Day Radar Outlook",
      description: "See future radar predictions hour by hour.",
      image: "🛰️"
    },
    {
      title: "AI Weather Insights",
      description: "Get intelligent summaries and activity recommendations.",
      image: "🤖"
    },
    {
      title: "Start Your Free Trial!",
      description: "Try all premium features free for 7 days.",
      image: "🎉"
    }
  ];

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="glass rounded-2xl p-8 max-w-md w-full text-center"
      >
        <div className="text-6xl mb-4">{steps[step].image}</div>
        <h2 className="text-2xl font-bold mb-2">{steps[step].title}</h2>
        <p className="text-white/70 mb-6">{steps[step].description}</p>
        
        {/* Progress dots */}
        <div className="flex justify-center gap-2 mb-6">
          {steps.map((s, idx) => (
            <div 
              key={`step-${s.title || idx}`}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === step ? 'bg-[#00f2ea]' : 'bg-white/20'
              }`}
            />
          ))}
        </div>
        
        <div className="flex gap-3">
          {step < steps.length - 1 ? (
            <>
              <Button 
                variant="ghost" 
                className="flex-1"
                onClick={onClose}
              >
                Skip
              </Button>
              <Button 
                className="flex-1 bg-[#00f2ea] hover:bg-[#00d4d4] text-black"
                onClick={() => setStep(step + 1)}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="ghost" 
                className="flex-1"
                onClick={onClose}
              >
                Maybe Later
              </Button>
              <Button 
                className="flex-1 bg-[#00f2ea] hover:bg-[#00d4d4] text-black"
                onClick={() => { onStartTrial?.(); onClose(); }}
              >
                <Zap className="w-4 h-4 mr-1" />
                Start Free Trial
              </Button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Trial Ending Banner
export const TrialEndingBanner = ({ daysLeft, onUpgrade }) => {
  if (daysLeft > 3) return null;

  const urgencyColor = daysLeft <= 1 ? 'from-red-500 to-orange-500' : 'from-yellow-500 to-orange-500';

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-r ${urgencyColor} rounded-xl p-4`}
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Timer className="w-5 h-5 text-white" />
          <span className="font-bold text-white">
            {daysLeft <= 0 
              ? "Your trial has ended!" 
              : `Your trial ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}!`
            }
          </span>
        </div>
        
        <Link to="/subscription">
          <Button size="sm" className="bg-white text-orange-600 hover:bg-white/90 font-semibold rounded-lg">
            Upgrade Now - Keep Access
          </Button>
        </Link>
      </div>
    </motion.div>
  );
};

// "What You're Missing" Section for Free Users
export const WhatYoureMissing = () => {
  const missedFeatures = [
    { 
      icon: "📅", 
      title: "15-Day Forecasts", 
      description: "See 10 more days ahead than free users",
      value: "Plan vacations, events & outdoor activities with confidence"
    },
    { 
      icon: "🛰️", 
      title: "3-Day Radar Outlook", 
      description: "Predict weather patterns before they arrive",
      value: "Never be caught off guard by storms again"
    },
    { 
      icon: "🤖", 
      title: "AI Weather Assistant", 
      description: "Get personalized weather insights",
      value: "Know exactly what to wear and when to plan"
    },
    { 
      icon: "✈️", 
      title: "Flight Weather", 
      description: "Check delays & turbulence forecasts",
      value: "Travel smarter with route-specific weather"
    },
    { 
      icon: "🌪️", 
      title: "Storm Chaser Streams", 
      description: "Live severe weather coverage",
      value: "Watch real-time storm tracking from pros"
    },
    { 
      icon: "💪", 
      title: "Health & Activity Index", 
      description: "Allergy, flu risk & activity conditions",
      value: "Optimize your outdoor activities"
    }
  ];

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="w-5 h-5 text-[#ff0080]" />
        <h3 className="font-bold text-lg">What You&apos;re Missing</h3>
      </div>
      
      <div className="grid gap-4">
        {missedFeatures.map((feature, idx) => (
          <motion.div
            key={`missed-${feature.title}`}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="flex items-start gap-3 p-3 bg-black/20 rounded-xl"
          >
            <span className="text-2xl">{feature.icon}</span>
            <div className="flex-1">
              <h4 className="font-semibold text-sm">{feature.title}</h4>
              <p className="text-xs text-white/60">{feature.description}</p>
              <p className="text-xs text-[#00f2ea] mt-1">{feature.value}</p>
            </div>
          </motion.div>
        ))}
      </div>
      
      <Link to="/subscription" className="block mt-4">
        <Button className="w-full bg-gradient-to-r from-[#ff0080] to-[#7000ff] hover:opacity-90 text-white font-semibold rounded-xl py-5">
          <Zap className="w-4 h-4 mr-2" />
          Unlock All Features - $14.99/year
        </Button>
      </Link>
    </div>
  );
};

// Exit Intent Popup
export const ExitIntentPopup = ({ isOpen, onClose, discount = 20 }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="glass rounded-2xl p-8 max-w-lg w-full text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/60 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="text-5xl mb-4">🎁</div>
          
          <h2 className="text-2xl font-bold mb-2">Wait! Don&apos;t leave empty-handed!</h2>
          <p className="text-white/70 mb-6">
            Get <span className="text-[#ff0080] font-bold">{discount}% OFF</span> your first year of Premium!
          </p>
          
          <div className="bg-gradient-to-r from-[#ff0080]/20 to-[#7000ff]/20 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-2xl line-through opacity-40">$14.99</span>
              <span className="text-3xl font-bold text-[#ff0080]">${(14.99 * (100 - discount) / 100).toFixed(2)}</span>
              <span className="text-white/60">/year</span>
            </div>
            <p className="text-sm text-white/60">That&apos;s less than $1/month for premium weather!</p>
          </div>
          
          <div className="space-y-3">
            <Link to="/subscription" className="block">
              <Button className="w-full bg-gradient-to-r from-[#ff0080] to-[#7000ff] hover:opacity-90 text-white font-semibold rounded-xl py-6">
                <Gift className="w-5 h-5 mr-2" />
                Claim My {discount}% Discount
              </Button>
            </Link>
            
            <Button variant="ghost" onClick={onClose} className="w-full text-white/60">
              No thanks, I&apos;ll pay full price later
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Enhanced Referral Program Component
export const ReferralProgram = ({ referralCode, referralCount = 0, referralLink }) => {
  const [copied, setCopied] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  
  const copyLink = () => {
    navigator.clipboard.writeText(referralLink || `https://theforecasttracker.com/ref/${referralCode}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareText = `Check out TheForecastTracker - the best weather app I've found! Use my link for a special discount: ${referralLink || `https://theforecasttracker.com/ref/${referralCode}`}`;

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#00f2ea] to-[#7000ff] flex items-center justify-center">
          <Gift className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="font-bold text-lg">Refer Friends, Get Rewards!</h3>
          <p className="text-sm text-white/60">Give 1 month free, Get 1 month free</p>
        </div>
      </div>
      
      <div className="bg-black/30 rounded-xl p-4 mb-4">
        <p className="text-sm text-white/60 mb-2">Your Referral Link</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-[#00f2ea] text-sm bg-black/30 p-2 rounded-lg truncate">
            {referralLink || `theforecasttracker.com/ref/${referralCode}`}
          </code>
          <Button size="sm" onClick={copyLink} className="bg-[#00f2ea] text-black hover:bg-[#00d4d4]">
            {copied ? <Check className="w-4 h-4" /> : "Copy"}
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-black/20 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-[#00f2ea]">{referralCount}</p>
          <p className="text-xs text-white/60">Friends Referred</p>
        </div>
        <div className="bg-black/20 rounded-xl p-3 text-center">
          <p className="text-2xl font-bold text-[#ff0080]">{referralCount}</p>
          <p className="text-xs text-white/60">Free Months Earned</p>
        </div>
      </div>
      
      <div className="relative">
        <Button 
          onClick={() => setShowShareOptions(!showShareOptions)}
          className="w-full bg-gradient-to-r from-[#00f2ea] to-[#7000ff] hover:opacity-90 text-white font-semibold rounded-xl"
        >
          <Users className="w-4 h-4 mr-2" />
          Share with Friends
        </Button>
        
        {showShareOptions && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute bottom-full left-0 right-0 mb-2 bg-black/90 rounded-xl p-3 space-y-2"
          >
            <a 
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center py-2 hover:bg-white/10 rounded-lg"
            >
              Share on Twitter/X
            </a>
            <a 
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center py-2 hover:bg-white/10 rounded-lg"
            >
              Share on Facebook
            </a>
            <a 
              href={`mailto:?subject=Check out TheForecastTracker&body=${encodeURIComponent(shareText)}`}
              className="block w-full text-center py-2 hover:bg-white/10 rounded-lg"
            >
              Share via Email
            </a>
          </motion.div>
        )}
      </div>
    </div>
  );
};

// Social Proof with Subscriber Count
export const LiveSubscriberCount = ({ count = 0 }) => {
  const [displayCount, setDisplayCount] = useState(0);
  const [recentSignup, setRecentSignup] = useState(null);

  useEffect(() => {
    // Animate count up
    const duration = 2000;
    const steps = 50;
    const increment = count / steps;
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= count) {
        setDisplayCount(count);
        clearInterval(timer);
      } else {
        setDisplayCount(Math.floor(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [count]);

  // Simulate recent signups
  useEffect(() => {
    const locations = ["New York", "Los Angeles", "Chicago", "Houston", "Phoenix", "Miami", "Seattle", "Denver"];
    const showRecentSignup = () => {
      const city = locations[Math.floor(Math.random() * locations.length)];
      setRecentSignup(city);
      setTimeout(() => setRecentSignup(null), 3000);
    };
    
    const interval = setInterval(showRecentSignup, 15000);
    showRecentSignup(); // Show one immediately
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-3">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex items-center justify-center gap-2 text-sm"
      >
        <Users className="w-4 h-4 text-[#00f2ea]" />
        <span>
          <span className="text-white font-bold">{displayCount.toLocaleString()}</span>
          <span className="text-white/60"> people upgraded this month</span>
        </span>
      </motion.div>
      
      <AnimatePresence>
        {recentSignup && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex items-center justify-center gap-2 bg-green-500/20 text-green-400 text-sm py-2 px-4 rounded-full mx-auto w-fit"
          >
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            Someone from {recentSignup} just subscribed!
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Grandfathered Pricing Promise Banner
export const GrandfatheredPricingBanner = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-4"
    >
      <div className="flex items-center gap-3">
        <Shield className="w-8 h-8 text-green-400 flex-shrink-0" />
        <div>
          <h4 className="font-bold text-green-400">Price Lock Guarantee</h4>
          <p className="text-sm text-white/70">
            Subscribe today and your rate is <strong>locked forever</strong>. Even when prices go up, you keep paying $14.99/year.
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Testimonials Component
export const Testimonials = () => {
  const testimonials = [
    {
      name: "Sarah M.",
      location: "Miami, FL",
      text: "The 15-day forecast has been a game-changer for planning beach trips. Worth every penny!",
      rating: 5
    },
    {
      name: "Michael T.",
      location: "Denver, CO",
      text: "As a pilot, the flight weather feature is incredibly useful. The turbulence predictions are surprisingly accurate.",
      rating: 5
    },
    {
      name: "Jennifer L.",
      location: "Seattle, WA",
      text: "Finally an app that tells me more than just 'partly cloudy'. The AI insights are like having a meteorologist in my pocket.",
      rating: 5
    }
  ];

  return (
    <div className="space-y-4">
      <h3 className="font-bold text-lg text-center mb-4">What Premium Users Say</h3>
      {testimonials.map((testimonial, idx) => (
        <motion.div
          key={`testimonial-${testimonial.name}`}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: idx * 0.2 }}
          className="glass rounded-xl p-4"
        >
          <div className="flex gap-1 mb-2">
            {[...Array(testimonial.rating)].map((_, i) => (
              <span key={`star-${i}`} className="text-yellow-400">★</span>
            ))}
          </div>
          <p className="text-sm text-white/80 italic mb-3">&ldquo;{testimonial.text}&rdquo;</p>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#00f2ea] to-[#7000ff] flex items-center justify-center text-sm font-bold">
              {testimonial.name[0]}
            </div>
            <div>
              <p className="text-sm font-semibold">{testimonial.name}</p>
              <p className="text-xs text-white/50">{testimonial.location}</p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// Free User Upgrade Prompt (inline within dashboard)
export const InlineUpgradePrompt = ({ feature }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gradient-to-r from-[#00f2ea]/10 to-[#7000ff]/10 border border-[#00f2ea]/30 rounded-xl p-4 my-4"
    >
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Crown className="w-6 h-6 text-yellow-400" />
          <div>
            <p className="font-semibold">Unlock {feature}</p>
            <p className="text-sm text-white/60">Upgrade to Premium for full access</p>
          </div>
        </div>
        <Link to="/subscription">
          <Button size="sm" className="bg-[#00f2ea] hover:bg-[#00d4d4] text-black font-semibold rounded-lg">
            <Zap className="w-4 h-4 mr-1" />
            Upgrade Now
          </Button>
        </Link>
      </div>
    </motion.div>
  );
};

// Limited Time Badge for urgency
export const LimitedTimeBadge = () => {
  return (
    <motion.div
      animate={{ scale: [1, 1.05, 1] }}
      transition={{ duration: 2, repeat: Infinity }}
      className="inline-flex items-center gap-1 bg-red-500/20 text-red-400 text-xs font-bold px-2 py-1 rounded-full"
    >
      <Timer className="w-3 h-3" />
      LIMITED TIME
    </motion.div>
  );
};
